/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/coldefs/protocol-value
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ResourceItemResourceLocationModal } from 'labstep-web/components/ResourceItem/ResourceLocationModal';
import { ICONS } from 'labstep-web/constants/icons';
import { EntityReadEntityContainer } from 'labstep-web/containers/Entity/Read/Entity';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolCondition/components/ColumnHeader';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import type { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { ResourceLocation } from 'labstep-web/models/resource-location.model';

export const getProtocolValueResourceLocationColumnDef = (
  experimentOrProtocol: Experiment | Protocol,
  name: string,
): SmartTableColDef => {
  return {
    colId: `protocol_value:${name}:resource_location`,
    sortable: true,
    headerName: 'Location',
    headerComponent: GridSmartTableColumnHeader,
    headerComponentParams: (params: HeaderComponentParamsProps) => {
      const constant =
        GridSmartTableProtocolConditionService.getProtocolValueConstant(
          experimentOrProtocol,
          name,
        );
      return {
        toolPanelColumnName: `Location - ${name}`,
        icon: ICONS.resource_location.primary,
        secondaryHeader: name,
        isConstant: !!constant,
        experimentOrProtocol,
        protocolValue:
          GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
            experimentOrProtocol,
            name,
          ) ||
          GridSmartTableProtocolConditionService.getProtocolValueConstant(
            experimentOrProtocol,
            name,
          ),
      };
    },
    editable: (params: EditableCallbackParams) =>
      GridSmartTableProtocolConditionService.getProtocolValueEditable(
        params,
        experimentOrProtocol,
        name,
      ),
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return '';
      }

      const protocolValue =
        GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
          params.node.data,
          experimentOrProtocol,
          name,
        ) ||
        GridSmartTableProtocolConditionService.getProtocolValueConstant(
          experimentOrProtocol,
          name,
        );
      return (
        protocolValue?.resourceItem?.resource_location?.guid || null
      );
    },
    valueSetter: (params) => {
      const newValue = params.newValue;
      if (
        !params.node ||
        newValue === '' ||
        params.newValue === params.oldValue
      ) {
        return false;
      }

      const protocolValue =
        GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
          params.node.data,
          experimentOrProtocol,
          name,
        ) ||
        GridSmartTableProtocolConditionService.getProtocolValueConstant(
          experimentOrProtocol,
          name,
        );

      if (protocolValue && protocolValue.resourceItem) {
        return GridSmartTableProtocolConditionService.updateEntity(
          params,
          ResourceItem.entityName,
          protocolValue.resourceItem.idAttr,
          {
            resource_location_id: newValue,
          },
        );
      }

      return false;
    },
    cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
      const variableTemplate =
        GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
          experimentOrProtocol,
          name,
        );

      const protocolValueConstant =
        GridSmartTableProtocolConditionService.getProtocolValueConstant(
          experimentOrProtocol,
          name,
        );

      if (!variableTemplate && !protocolValueConstant) {
        return null;
      }

      const protocolValue =
        GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
          params.node.data,
          experimentOrProtocol,
          name,
        ) || protocolValueConstant;

      if (!protocolValue || !protocolValue.resourceItem) {
        return null;
      }
      if (protocolValue.resourceItem.resource_location) {
        return (
          <EntityReadEntityContainer
            entityName={ResourceItem.entityName}
            id={protocolValue.resourceItem.id}
          >
            {({ entity: resourceItem }): JSX.Element => {
              return (
                <ParamsStateContextProvider value={{}}>
                  <ResourceItemResourceLocationModal
                    resourceItem={resourceItem}
                  />
                </ParamsStateContextProvider>
              );
            }}
          </EntityReadEntityContainer>
        );
      }

      return (
        <DataGridPlaceholder
          params={params}
          children={`Specify ${ResourceLocation.getHumanReadableEntityName()}`}
          editable={GridSmartTableProtocolConditionService.getProtocolValueEditable(
            params,
            experimentOrProtocol,
            name,
          )}
          placeholder={`No ${ResourceLocation.getHumanReadableEntityName()}`}
        />
      );
    },
    cellEditor: (params: CellEditorParams<ProtocolCondition>) => {
      if (!params.node) {
        return null;
      }
      const protocolValue =
        GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
          params.node.data,
          experimentOrProtocol,
          name,
        ) ||
        GridSmartTableProtocolConditionService.getProtocolValueConstant(
          experimentOrProtocol,
          name,
        );

      if (!protocolValue || !protocolValue.resourceItem) {
        return null;
      }
      return (
        <EntityReadEntityContainer
          entityName={ResourceItem.entityName}
          id={protocolValue.resourceItem.id}
        >
          {({ entity: resourceItem }): JSX.Element => {
            return (
              <ParamsStateContextProvider value={{}}>
                <ResourceItemResourceLocationModal
                  resourceItem={resourceItem}
                  hideMap
                  modalProps={{
                    open: true,
                    viewComponent: null,
                    onClose: (): void => {
                      params.api.stopEditing();
                      params.api.clearRangeSelection();

                      const rowIndex =
                        'rowIndex' in params ? params.rowIndex : null;
                      if (rowIndex !== null) {
                        params.api.setFocusedCell(
                          rowIndex + 1,
                          params.column,
                        );
                      }
                    },
                  }}
                />
              </ParamsStateContextProvider>
            );
          }}
        </EntityReadEntityContainer>
      );
    },
    cellStyle: (params) =>
      GridSmartTableProtocolConditionService.getCellStyle(
        params,
        experimentOrProtocol,
        ProtocolValue.entityName,
        name,
      ),
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: () => {
      return null;
    },
  };
};
